<script setup>

import {useSettingsStore} from "@/store/settings";
import {computed, onMounted, reactive} from "vue";
import FormInput from "@/components/ui/FormInput.vue";
import PhoneInput from "@/components/ui/PhoneInput.vue";
import ActionButton from "@/components/ui/ActionButton.vue";
import CheckBox from "@/components/ui/CheckBox.vue";
import FileButton from "@/components/ui/FileButton.vue";
import {useUsersStore} from "@/store/users";
import {useToast} from "vue-toast-notification";
import {checkEmail, checkPhone} from "@/tools/validation.tools";
import {openImagePreview} from "@/composables/image-preview";
import avatarFallback from "@/assets/img/Avatar.png";


const userInfo =  reactive ({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    emailSubscribed: false,
})
const settings = useSettingsStore()
const usersStore = useUsersStore()
const toast =useToast()

const avatarUrl = computed(() => settings.user.img_url || avatarFallback);
const isAtClub = computed(() => !!settings.user.is_at_club);

const emailError = computed(() => checkEmail(userInfo.email, true) ? '' : 'Неверный формат email');
const phoneError = computed(() => checkPhone(userInfo.phone, true) ? '' : 'Введите полный номер телефона');

const dataChanged = computed(() => {
  return userInfo.firstName != settings.user.first_name
      || userInfo.lastName != settings.user.last_name
      || userInfo.email != settings.user.email
      || userInfo.phone != settings.user.phone
})

const canSave = computed(() => dataChanged.value && !emailError.value && !phoneError.value);

async function avatarUploadHandler(file) {
  if (!file) {
    return;
  }
  const ok = await usersStore.uploadUserImage(file);
  if (usersStore.error) {
    toast.error(usersStore.error)
    return
  }
  if (ok) {
    toast.success("Аватар обновлён")
    await settings.loadSetting()
  }
}

async function saveHandler() {
  if(usersStore.loading || !canSave.value) {
    return;
  }
  await usersStore.updateUser({
    first_name: userInfo.firstName,
    last_name: userInfo.lastName,
    email: userInfo.email,
    phone: userInfo.phone}
  )
  if (usersStore.error) {
    toast.error(usersStore.error)
    return
  }
  toast.success("Пользователь обновлен")
  await settings.loadSetting()
  fillData()
}

async function updateEmailSubscriptionHandler(emailSubscribed) {
  if (usersStore.loading) {
    userInfo.emailSubscribed = !!settings.user.email_subscribed;
    return;
  }
  const user = await usersStore.updateEmailSubscription(emailSubscribed);
  if (usersStore.error || !user) {
    userInfo.emailSubscribed = !!settings.user.email_subscribed;
    toast.error(usersStore.error || "Не удалось обновить настройки рассылки");
    return;
  }
  settings.user = user;
  toast.success(emailSubscribed ? "Подписка на рассылку включена" : "Вы отказались от рассылки");
}

onMounted(() => {
  fillData()
})

function fillData() {
  userInfo.firstName = settings.user.first_name;
  userInfo.lastName = settings.user.last_name;
  userInfo.email = settings.user.email;
  userInfo.phone = settings.user.phone;
  userInfo.emailSubscribed = !!settings.user.email_subscribed;
}

</script>

<template>
<div class="contactsView">

   <h2 class="contactsView__header">
     <img
         class="contactsView__image"
         :src="avatarUrl"
         alt=""
         @click="openImagePreview(settings.user.img_url)"
     >
     <div class="contactsView__name"> {{userInfo.firstName}} {{userInfo.lastName}} </div>
      <div class="contactsView__spacer"></div>
     <ActionButton :disabled="!canSave" style="background-color: #6FC062; color: #212226;" @buttonClick="saveHandler">
       <template #caption>Сохранить</template>
     </ActionButton>

   </h2>
    <div class="contactsView__userInfo">
        <h3 class="contactsView__title">Ваши данные</h3>

      <div v-if="isAtClub" class="contactsView__line">
        <label class="contactsView__textLabel">Аватар</label>
        <FileButton accept="image/jpeg,image/png,image/gif" @select="avatarUploadHandler"/>
      </div>

      <div class="contactsView__line">
        <label class="contactsView__textLabel">Имя</label>
        <form-input v-model="userInfo.firstName" :name-only="true"/>
      </div>

      <div class="contactsView__line">
        <label class="contactsView__textLabel">Фамилия</label>
        <form-input v-model="userInfo.lastName" :name-only="true"/>
      </div>

      <div class="contactsView__line">
        <label class="contactsView__textLabel">Email</label>
        <form-input v-model="userInfo.email" :error="emailError"/>
      </div>

      <div class="contactsView__line">
        <label class="contactsView__textLabel">Телефон</label>
        <phone-input v-model="userInfo.phone" :error="phoneError"/>
      </div>

      <div class="contactsView__line">
        <CheckBox
            v-model="userInfo.emailSubscribed"
            text="Получать новости и специальные предложения по email"
            @change="updateEmailSubscriptionHandler"
        />
      </div>
    </div>
</div>
</template>

<style scoped lang="scss">
.contactsView {
  display: flex;
  flex-direction: column;
  text-align: left;
  gap: 14px;
  &__header {
    height: 113px;
    gap: 10px;
    opacity: 1;
    border-radius: 20px;
    background: var(--cd-card-bg);
    display: flex;
    align-items: center;
    margin: 0;
    padding: 24px;
    font-family: Nunito Sans;
    font-weight: 400;
    font-size: 16px;
    line-height: 100%;
    text-align: center;

  }
  &__userInfo {
    padding: 24px;

    min-height: 476px;
    gap: 24px;
    opacity: 1;
    border-radius: 20px;
    display: flex;
    flex-direction: column;
    background: var(--cd-card-bg);
  }
  &__spacer {
    flex-grow: 1000;
  }

  &__image {
    width: 64px;
    height: 64px;
    min-width: 64px;
    border-radius: 100%;
    object-fit: cover;
    display: block;
    flex-shrink: 0;
    cursor: zoom-in;
  }

}
</style>
